import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Eye, TrendingUp, ChevronUp, ChevronDown } from 'lucide-react';
import { ProposalNodeSet, GlobalCosts, ProposalDiscount, DesignEstimatingData } from '../../types';
import { ProposalRateSettings } from '../../utils/appSettings';
import { applyProfitMargin, getProposalDiscountAmount } from '../../utils/proposalCalculations';
import { estimatingRaw, type DesignRateInputs } from '../../utils/proposalDesignBreakdown';
import { productionRawForSpecies } from '../../utils/proposalSpeciesProduction';
import { computeExclusiveStaffEconomics, nodeSetsForExclusiveSelection } from '../../utils/proposalExclusiveEconomics';
import { projectCostPerSqFt } from '../../utils/projectCostPerSqFt';
import { computeBondingAmount, effectiveBondingPercent, formatBondingPercent } from '../../utils/proposalBonding';
import './ProposalSummary.css';

interface KitSpeciesPricing {
  speciesName: string;
  totalCost: number;
}

interface ProposalSummaryProps {
  nodeSets: ProposalNodeSet[];
  profitMargin: number;
  onProfitMarginChange: (margin: number) => void;
  onPreview: () => void;
  installOptional?: boolean;
  onInstallOptionalChange?: (value: boolean) => void;
  designIncludedInKit?: boolean;
  onDesignIncludedChange?: (value: boolean) => void;
  isRomPricing?: boolean;
  onRomChange?: (value: boolean) => void;
  /** Optional whole-project square footage (not per structure). */
  projectSquareFootage?: number;
  onProjectSquareFootageChange?: (value: number | undefined) => void;
  /** Client-facing toggle — builder footer only shows $/sf when this is on. */
  showCostPerSqFt?: boolean;
  kitMode?: boolean;
  activeSpecies?: string;
  kitPricing?: KitSpeciesPricing[];
  isSent?: boolean;
  globalCosts?: GlobalCosts;
  /** From Settings → Proposals: % of (structure raw + global costs) added before margin. */
  overheadPercentOfProject?: number;
  /**
   * When set (signed proposal), footer line items and total mirror the client contract
   * (`computeProposalGrandTotals` + Firestore install/species), not raw node rollups.
   */
  signedContractTotals?: {
    design: number;
    timberKit: number;
    installWithTravel: number;
    /** Sign-time bonding premium already inside `total`. */
    bonding?: number;
    total: number;
  } | null;
  /** Frozen client species choice — shown on Timber Kit when signed. */
  signedSpeciesLabel?: string | null;
  /** Promotional discount (flat dollars off the post-margin total). */
  discount?: ProposalDiscount;
  designEstimating?: DesignEstimatingData | null;
  proposalStatus?: string | null;
  designRates?: DesignRateInputs | ProposalRateSettings | null;
}

const projectOverheadRaw = (baseRaw: number, pct: number): number => {
  const p = Math.max(0, pct || 0);
  if (p <= 0 || baseRaw <= 0) return 0;
  return Math.round(baseRaw * (p / 100));
};

const ProposalSummary: React.FC<ProposalSummaryProps> = ({
  nodeSets,
  profitMargin,
  onProfitMarginChange,
  onPreview,
  installOptional = false,
  onInstallOptionalChange,
  // Default false so undefined (legacy / not yet loaded) does not look "included".
  designIncludedInKit = false,
  onDesignIncludedChange,
  isRomPricing = false,
  onRomChange,
  projectSquareFootage,
  onProjectSquareFootageChange,
  showCostPerSqFt = false,
  kitMode = false,
  activeSpecies,
  kitPricing,
  isSent = false,
  globalCosts,
  overheadPercentOfProject = 0,
  signedContractTotals = null,
  signedSpeciesLabel = null,
  discount,
  designEstimating,
  proposalStatus,
  designRates,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  // Track which species breakdown row is selected for main footer
  const [selectedSpeciesIndex, setSelectedSpeciesIndex] = useState(0);
  // Collapse/expand the species breakdown panel
  const [isBreakdownExpanded, setIsBreakdownExpanded] = useState(true);
  // Show install settings popover
  const [showInstallSettings, setShowInstallSettings] = useState(false);
  // Show design included in kit settings popover
  const [showDesignSettings, setShowDesignSettings] = useState(false);
  // Centered Total dialog (ROM + project square footage)
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [sqftDraft, setSqftDraft] = useState('');
  // Show timber kit species popover
  const [showTimberKitSettings, setShowTimberKitSettings] = useState(false);
  // Staff totals price the default Choose one pick (same as the public proposal).
  const pricedNodeSets = useMemo(() => nodeSetsForExclusiveSelection(nodeSets), [nodeSets]);

  const estimatingDesign = estimatingRaw(designEstimating, designRates, proposalStatus);

  const exclusiveStaff = useMemo(
    () =>
      computeExclusiveStaffEconomics(nodeSets, profitMargin, globalCosts, {
        isKitProposal: kitMode,
        kitPricing,
        activeSpecies,
        discountAmount: getProposalDiscountAmount(discount),
        designEstimating,
        designEstimatingRates: designRates,
        proposalStatus,
      }),
    [nodeSets, profitMargin, globalCosts, kitMode, kitPricing, activeSpecies, discount, designEstimating, designRates, proposalStatus],
  );

  // Check if any structure has multiple species
  const hasMultipleSpecies = useMemo(() => {
    return pricedNodeSets.some(ns =>
      ns.materials.quoteMultipleSpecies &&
      (ns.materials.alternateSpecies?.length ?? 0) > 0
    );
  }, [pricedNodeSets]);

  // Calculate species breakdown for timber kit costs
  const speciesBreakdown = useMemo(() => {
    if (!hasMultipleSpecies) return [];

    // Calculate design and install totals (these don't change by species)
    let designTotal = 0;
    let installTotal = 0;
    pricedNodeSets.forEach(ns => {
      const qty = ns.quantity || 1;
      designTotal += ns.design.total * qty;
      installTotal += (ns.install.total + ns.travel.total + (ns.install.overheadTotal || 0)) * qty;
    });
    // Get primary species name for display (from first structure's first timber item)
    const primarySpeciesName = pricedNodeSets[0]?.materials.timberList[0]?.species || 'Default';

    // Calculate default/primary timber kit total with per-structure breakdown
    const primaryStructureDetails = pricedNodeSets.map(ns => {
      const qty = ns.quantity || 1;
      return {
        name: ns.name,
        timberKit: (ns.production.total + ns.materials.total + ns.freight.total + ns.overhead.total) * qty,
      };
    });
    const primaryTimberKit = primaryStructureDetails.reduce((sum, s) => sum + s.timberKit, 0);
    const primaryRawCost = designTotal + primaryTimberKit + installTotal;
    const primaryBillable = applyProfitMargin(primaryRawCost, profitMargin);
    const primaryProfit = primaryBillable - primaryRawCost;

    const breakdown = [{
      name: `${primarySpeciesName} (Default)`,
      timberKit: primaryTimberKit,
      rawCost: primaryRawCost,
      profit: primaryProfit,
      billable: primaryBillable,
      isPrimary: true,
      structureDetails: primaryStructureDetails,
    }];

    // Get ALL alternate species names across all structures (no filtering)
    const alternateNames = new Set<string>();
    pricedNodeSets.forEach(ns => {
      (ns.materials.alternateSpecies || []).forEach(s => {
        alternateNames.add(s.speciesName);
      });
    });

    // Calculate totals for each alternate species with per-structure breakdown
    alternateNames.forEach(speciesName => {
      const structureDetails = pricedNodeSets.map(ns => {
        const qty = ns.quantity || 1;
        const altSpecies = (ns.materials.alternateSpecies || []).find(
          s => s.speciesName === speciesName
        );
        if (altSpecies) {
          // This structure has this alternate configured - timber + production for that species
          const otherMaterialsCost = ns.materials.total - ns.materials.timberTotal;
          return {
            name: ns.name,
            timberKit: (productionRawForSpecies(ns, speciesName) + otherMaterialsCost + altSpecies.timberTotal + ns.freight.total + ns.overhead.total) * qty,
            hasAlternate: true,
          };
        }
        // This structure doesn't have this alternate - use its default materials
        return {
          name: ns.name,
          timberKit: (ns.production.total + ns.materials.total + ns.freight.total + ns.overhead.total) * qty,
          hasAlternate: false,
        };
      });

      const altTimberKit = structureDetails.reduce((sum, s) => sum + s.timberKit, 0);
      const altRawCost = designTotal + altTimberKit + installTotal;
      const altBillable = applyProfitMargin(altRawCost, profitMargin);
      const altProfit = altBillable - altRawCost;

      breakdown.push({
        name: speciesName,
        timberKit: altTimberKit,
        rawCost: altRawCost,
        profit: altProfit,
        billable: altBillable,
        isPrimary: false,
        structureDetails,
      });
    });

    return breakdown;
  }, [pricedNodeSets, profitMargin, hasMultipleSpecies, estimatingDesign]);

  const globalCostTotal = (globalCosts?.totalGlobalCosts || 0) + estimatingDesign;
  const showDesignTimberRows = !kitMode || !!signedContractTotals;

  const totals = useMemo(() => {
    if (signedContractTotals) {
      return {
        designTotal: signedContractTotals.design,
        timberKitTotal: signedContractTotals.timberKit,
        installTotal: signedContractTotals.installWithTravel,
        globalCostTotal: 0,
        projectOverheadAmount: 0,
        rawCostTotal: 0,
        grossProfit: 0,
        billableTotal: signedContractTotals.total,
      };
    }

    // Kit mode pricing - use kit's species pricing directly
    if (kitMode && kitPricing && kitPricing.length > 0 && activeSpecies) {
      const selectedPricing = kitPricing.find(kp => kp.speciesName === activeSpecies);
      if (selectedPricing) {
        let installRawTotal = 0;
        pricedNodeSets.forEach(ns => {
          const qty = ns.quantity || 1;
          installRawTotal += (ns.install.total + ns.travel.total) * qty;
        });

        const kitBillable = selectedPricing.totalCost;
        const installBillable = applyProfitMargin(installRawTotal, profitMargin);

        let structureRawForOverhead = 0;
        pricedNodeSets.forEach(ns => {
          const qty = ns.quantity || 1;
          structureRawForOverhead +=
            (ns.design.total +
              ns.production.total +
              ns.materials.total +
              ns.freight.total +
              ns.overhead.total +
              ns.install.total +
              ns.travel.total +
              (ns.install.overheadTotal || 0)) *
            qty;
        });
        const projectOverheadAmount = projectOverheadRaw(
          structureRawForOverhead + globalCostTotal,
          overheadPercentOfProject
        );
        const rawCostTotal = kitBillable + installRawTotal + globalCostTotal + projectOverheadAmount;
        const billableTotal =
          kitBillable +
          installBillable +
          applyProfitMargin(globalCostTotal, profitMargin) +
          applyProfitMargin(projectOverheadAmount, profitMargin);
        const grossProfit = billableTotal - rawCostTotal;

        return {
          designTotal: 0,
          timberKitTotal: kitBillable,
          installTotal: installBillable,
          globalCostTotal,
          projectOverheadAmount,
          rawCostTotal,
          grossProfit,
          billableTotal,
          selectedSpeciesName: activeSpecies,
        };
      }
    }

    // If multi-species enabled and a species is selected, use that species' breakdown values
    if (hasMultipleSpecies && speciesBreakdown.length > 0) {
      const selected = speciesBreakdown[selectedSpeciesIndex] || speciesBreakdown[0];
      let designTotal = 0;
      let installTotal = 0;
      pricedNodeSets.forEach(ns => {
        const qty = ns.quantity || 1;
        designTotal += ns.design.total * qty;
        installTotal += (ns.install.total + ns.travel.total + (ns.install.overheadTotal || 0)) * qty;
      });

      const projectOverheadAmount = projectOverheadRaw(
        selected.rawCost + globalCostTotal,
        overheadPercentOfProject
      );
      const rawCostTotal = selected.rawCost + globalCostTotal + projectOverheadAmount;
      const billableTotal = applyProfitMargin(rawCostTotal, profitMargin);
      const grossProfit = billableTotal - rawCostTotal;

      return {
        designTotal,
        timberKitTotal: selected.timberKit,
        installTotal,
        globalCostTotal,
        projectOverheadAmount,
        rawCostTotal,
        grossProfit,
        billableTotal,
        selectedSpeciesName: selected.name,
      };
    }

    // Default calculation when multi-species not enabled
    let designTotal = 0;
    let timberKitTotal = 0;
    let installTotal = 0;

    pricedNodeSets.forEach(ns => {
      const qty = ns.quantity || 1;
      designTotal += ns.design.total * qty;
      timberKitTotal += (ns.production.total + ns.materials.total + ns.freight.total + ns.overhead.total) * qty;
      installTotal += (ns.install.total + ns.travel.total + (ns.install.overheadTotal || 0)) * qty;
    });

    const projectOverheadAmount = projectOverheadRaw(
      designTotal + timberKitTotal + installTotal + globalCostTotal,
      overheadPercentOfProject
    );
    const rawCostTotal = designTotal + timberKitTotal + installTotal + globalCostTotal + projectOverheadAmount;
    const billableTotal = applyProfitMargin(rawCostTotal, profitMargin);
    const grossProfit = billableTotal - rawCostTotal;

    return {
      designTotal,
      timberKitTotal,
      installTotal,
      globalCostTotal,
      projectOverheadAmount,
      rawCostTotal,
      grossProfit,
      billableTotal,
    };
  }, [pricedNodeSets, profitMargin, hasMultipleSpecies, speciesBreakdown, selectedSpeciesIndex, kitMode, activeSpecies, kitPricing, globalCostTotal, overheadPercentOfProject, signedContractTotals, estimatingDesign]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Promotional discount comes off the post-margin total. Signed totals are
  // already discounted (frozen at sign time), so only apply it live.
  const discountAmount = signedContractTotals
    ? 0
    : Math.min(getProposalDiscountAmount(discount), Math.max(0, totals.billableTotal));
  // Bonding is a client pass-through on the discounted subtotal (no margin, not
  // in raw cost or gross profit). Signed proposals show the frozen premium.
  const bondingPercent = effectiveBondingPercent(globalCosts);
  const bondingAmount = signedContractTotals
    ? (signedContractTotals.bonding ?? 0)
    : computeBondingAmount(totals.billableTotal - discountAmount, bondingPercent);
  const displayBillableTotal = totals.billableTotal - discountAmount + (signedContractTotals ? 0 : bondingAmount);
  const displayGrossProfit = totals.grossProfit - discountAmount;
  const canOpenPricingDialog = !!(onRomChange || onProjectSquareFootageChange);
  const persistedSqft = projectSquareFootage && projectSquareFootage > 0
    ? Math.round(projectSquareFootage)
    : undefined;
  const draftSqft = (() => {
    const n = Number(sqftDraft.trim());
    return Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
  })();
  const liveCostPerSqFt = projectCostPerSqFt(displayBillableTotal, draftSqft ?? persistedSqft);
  const publicCostPerSqFt = showCostPerSqFt
    ? projectCostPerSqFt(displayBillableTotal, persistedSqft)
    : null;

  const commitSqft = useCallback(() => {
    if (!onProjectSquareFootageChange) return;
    const n = Number(sqftDraft.trim());
    const next = Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
    if (next !== persistedSqft) onProjectSquareFootageChange(next);
  }, [onProjectSquareFootageChange, sqftDraft, persistedSqft]);

  const closePricingDialog = useCallback(() => {
    commitSqft();
    setShowPricingDialog(false);
  }, [commitSqft]);

  useEffect(() => {
    if (!showPricingDialog) return;
    setSqftDraft(persistedSqft ? String(persistedSqft) : '');
  }, [showPricingDialog, persistedSqft]);

  useEffect(() => {
    if (!showPricingDialog) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closePricingDialog();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showPricingDialog, closePricingDialog]);

  return (
    <div className="proposal-summary-wrapper">
      <div className="proposal-summary-bar">

        {/* Cost Breakdown */}
        <div className="cost-group">
          {/* Design - Hidden in Kit Mode */}
          {!showDesignTimberRows ? null : (
            <div className="cost-item design-item">
              <button
                className={`cost-label clickable ${designIncludedInKit ? 'optional-active' : ''} ${isSent ? 'disabled' : ''}`}
                onClick={() => !isSent && onDesignIncludedChange && setShowDesignSettings(prev => !prev)}
                title={isSent ? "Cannot modify when proposal is sent" : "Click to configure design display"}
                style={isSent ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                Design
                {designIncludedInKit && <span className="optional-badge">In Kit</span>}
              </button>
              <span className="cost-value">{formatCurrency(totals.designTotal)}</span>

              {/* Design Included Settings Popover */}
              {showDesignSettings && onDesignIncludedChange && !isSent && (
                <div className="install-popover">
                  <div className="popover-header">Design Display</div>
                  <label className="popover-option">
                    <input
                      type="checkbox"
                      checked={designIncludedInKit}
                      onChange={(e) => {
                        onDesignIncludedChange(e.target.checked);
                        setShowDesignSettings(false);
                      }}
                    />
                    <span>Show design as &ldquo;Included&rdquo; in Timber Kit</span>
                  </label>
                  <p className="popover-hint">When enabled, the public proposal shows Design as "Included" and rolls the cost into the Timber Kit bucket.</p>
                </div>
              )}
            </div>
          )}

          {/* Timber Kit - Hidden in Kit Mode unless showing signed contract mirror */}
          {showDesignTimberRows && (
            <div className="cost-item timber-kit-item">
              <button
                className={`cost-label clickable ${hasMultipleSpecies ? '' : ''} ${isSent ? 'disabled' : ''}`}
                onClick={() => !isSent && hasMultipleSpecies && setShowTimberKitSettings(prev => !prev)}
                title={isSent ? "Cannot modify when proposal is sent" : hasMultipleSpecies ? 'Click to select species' : 'Timber Kit'}
                style={{ cursor: isSent ? 'not-allowed' : hasMultipleSpecies ? 'pointer' : 'default', opacity: isSent ? 0.5 : 1 }}
              >
                Timber Kit
                {(signedSpeciesLabel ||
                  (hasMultipleSpecies && speciesBreakdown[selectedSpeciesIndex])) && (
                  <span className="species-badge">
                    {(signedSpeciesLabel ||
                      speciesBreakdown[selectedSpeciesIndex]?.name ||
                      ''
                    ).replace(' (Default)', '')}
                  </span>
                )}
              </button>
              <span className="cost-value">{formatCurrency(totals.timberKitTotal)}</span>

              {/* Timber Kit Species Popover — unsigned proposals only */}
              {showTimberKitSettings && hasMultipleSpecies && speciesBreakdown.length > 0 && !signedContractTotals && (
                <div className="install-popover timber-popover">
                  <div className="popover-header">Select Species</div>
                  {speciesBreakdown.map((species, index) => (
                    <label
                      key={species.name}
                      className={`popover-option species-option ${selectedSpeciesIndex === index ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedSpeciesIndex(index);
                        setShowTimberKitSettings(false);
                      }}
                    >
                      <span className="species-radio">{selectedSpeciesIndex === index ? '●' : '○'}</span>
                      <span className="species-info">
                        <span className="species-name-option">{species.name}</span>
                        <span className="species-price">{formatCurrency(species.timberKit)}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="cost-item install-item">
            <button
              className={`cost-label clickable ${installOptional ? 'optional-active' : ''} ${isSent ? 'disabled' : ''}`}
              onClick={() => !isSent && onInstallOptionalChange && setShowInstallSettings(prev => !prev)}
              title={isSent ? "Cannot modify when proposal is sent" : "Click to configure install options"}
              style={isSent ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              Install
              {installOptional && <span className="optional-badge">Optional</span>}
            </button>
            <span className="cost-value">{formatCurrency(totals.installTotal)}</span>

            {/* Install Settings Popover - only show when not sent */}
            {showInstallSettings && onInstallOptionalChange && !isSent && (
              <div className="install-popover">
                <div className="popover-header">Install Settings</div>
                <label className="popover-option">
                  <input
                    type="checkbox"
                    checked={installOptional}
                    onChange={(e) => {
                      onInstallOptionalChange(e.target.checked);
                      setShowInstallSettings(false);
                    }}
                  />
                  <span>Make install optional for clients</span>
                </label>
                <p className="popover-hint">When enabled, clients can choose whether to include installation in their quote.</p>
              </div>
            )}
          </div>

          {/* Global / overhead / raw are rolled into signed bucket totals — hide to avoid double-counting */}
          {!signedContractTotals && globalCostTotal > 0 && (
            <div className="cost-item project-cost">
              <span className="cost-label">
                Project
                <span className="project-cost-badge">Global</span>
              </span>
              <span className="cost-value">{formatCurrency(globalCostTotal)}</span>
            </div>
          )}

          {!signedContractTotals && totals.projectOverheadAmount != null && totals.projectOverheadAmount > 0 && (
            <div className="cost-item project-cost">
              <span className="cost-label">
                Project overhead
                <span className="project-cost-badge">{overheadPercentOfProject}%</span>
              </span>
              <span className="cost-value">{formatCurrency(totals.projectOverheadAmount)}</span>
            </div>
          )}

          {!signedContractTotals && <div className="cost-divider" />}

          {!signedContractTotals && (
          <div className="cost-item raw-cost">
            <span className="cost-label">{kitMode ? 'Kit Price' : 'Raw Cost'}</span>
            <span className="cost-value">{formatCurrency(kitMode ? totals.timberKitTotal : totals.rawCostTotal)}</span>
          </div>
          )}
        </div>

        {/* Profit Control */}
        {!signedContractTotals && (
        <div className="profit-group">
          <span className="profit-percent">{profitMargin}%</span>
          <div
            className={`profit-slider-track${isSent ? ' disabled' : ''}`}
            ref={sliderRef}
            onMouseDown={(e) => {
              if (isSent) return;
              const handleMove = (ev: MouseEvent) => {
                if (!sliderRef.current) return;
                const rect = sliderRef.current.getBoundingClientRect();
                const pct = Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
                onProfitMarginChange(Math.round(pct * 60));
              };
              handleMove(e.nativeEvent);
              const handleUp = () => {
                window.removeEventListener('mousemove', handleMove);
                window.removeEventListener('mouseup', handleUp);
              };
              window.addEventListener('mousemove', handleMove);
              window.addEventListener('mouseup', handleUp);
            }}
          >
            <div className="profit-slider-fill" style={{ width: `${(profitMargin / 60) * 100}%` }} />
            <div className="profit-slider-thumb" style={{ left: `${(profitMargin / 60) * 100}%` }} />
          </div>
          <span className="profit-value">{formatCurrency(displayGrossProfit)}</span>
          {kitMode && <span className="profit-note">Install Only</span>}
        </div>
        )}

        {/* Total */}
        <div className={`total-group ${canOpenPricingDialog ? 'clickable' : ''}`}>
          {discountAmount > 0 && (
            <div
              className="summary-discount-row"
              title={discount?.clientDescription || discount?.internalNotes || undefined}
            >
              <span className="summary-discount-label">{discount?.label || 'Discount'}</span>
              <span className="summary-discount-amount">−{formatCurrency(discountAmount)}</span>
            </div>
          )}
          {bondingAmount > 0 && (
            <div
              className="summary-discount-row summary-bonding-row"
              title={`Surety bonding pass-through — ${formatBondingPercent(bondingPercent)} of the client total, no margin. Shown as its own line below Installation on the client proposal.`}
            >
              <span className="summary-discount-label">Bonding {bondingPercent > 0 ? formatBondingPercent(bondingPercent) : ''}</span>
              <span className="summary-discount-amount">+{formatCurrency(bondingAmount)}</span>
            </div>
          )}
          <button
            type="button"
            className="total-label-wrap"
            onClick={() => {
              if (!canOpenPricingDialog) return;
              setSqftDraft(persistedSqft ? String(persistedSqft) : '');
              setShowPricingDialog(true);
            }}
            title={canOpenPricingDialog ? 'Click to configure ROM and project square footage' : undefined}
          >
            <span className="total-label">Total</span>
            {isRomPricing && <span className="rom-badge">ROM</span>}
            <span className="total-figures">
              <span className="total-value-row">
                {publicCostPerSqFt != null && (
                  <span className="total-psf-badge">${publicCostPerSqFt}/sf</span>
                )}
                <span className="total-value">{formatCurrency(displayBillableTotal)}</span>
              </span>
              {!signedContractTotals && exclusiveStaff.billableRange && (
                <span className="choose-one-range">
                  Choose one range {formatCurrency(exclusiveStaff.billableRange.min)}–{formatCurrency(exclusiveStaff.billableRange.max)}
                </span>
              )}
            </span>
            {signedContractTotals && (
              <span style={{ display: 'block', fontSize: 11, opacity: 0.78, fontWeight: 500, marginTop: 2 }}>
                Same total as the client-facing signed proposal
              </span>
            )}
          </button>
        </div>

        {showPricingDialog && createPortal(
          <div
            className="total-pricing-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="total-pricing-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) closePricingDialog();
            }}
          >
            <div className="total-pricing-dialog" onClick={(e) => e.stopPropagation()}>
              <header className="total-pricing-header">
                <div>
                  <h3 id="total-pricing-title">Total</h3>
                  <p>ROM pricing and optional project square footage.</p>
                </div>
              </header>
              <div className="total-pricing-body">
                <label className={`total-pricing-option${isSent || !onRomChange ? ' is-locked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={!!isRomPricing}
                    disabled={isSent || !onRomChange}
                    onChange={(e) => onRomChange?.(e.target.checked)}
                  />
                  <span>ROM (Rough Order of Magnitude)</span>
                </label>
                {isSent && (
                  <p className="total-pricing-locked">ROM is locked after the proposal is sent.</p>
                )}
                <p className="total-pricing-hint">
                  When enabled, the client proposal will indicate this is preliminary pricing that needs refinement after their input.
                </p>

                <label className="total-pricing-field">
                  <span>Project square footage</span>
                  <input
                    type="number"
                    min={0}
                    inputMode="numeric"
                    placeholder="Optional"
                    value={sqftDraft}
                    onChange={(e) => setSqftDraft(e.target.value)}
                    onBlur={commitSqft}
                  />
                </label>
                {liveCostPerSqFt != null && (
                  <p className="total-pricing-preview">= ${liveCostPerSqFt}/sf</p>
                )}
              </div>
              <footer className="total-pricing-footer">
                <button type="button" className="total-pricing-done" onClick={closePricingDialog}>
                  Done
                </button>
              </footer>
            </div>
          </div>,
          document.body,
        )}

        {/* Status badge */}
        <div className="action-group">
          {isSent && (
            <div className="status-indicator sent" style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, background: 'rgba(44,62,47,0.08)', color: '#2C3E2F', fontWeight: 600 }}>
              <span className="status-dot"></span>
              Sent
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProposalSummary;
